// Income and left to budget (item F): the schedule in words, the Budget tab's Income group and
// headline, the income line sheet, and the Overview line and card. Income is never a transaction.
import type { IncomeLineDto, OverviewDto } from '../../../shared/api';
import { esc, money, shortDate } from '../format';

const ord = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;
const weekday = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
};

/** "Monthly on the 1st", "Twice a month: 1st and 15th", "Every two weeks from Oct 3", "Every week on Fridays". */
export function scheduleText(l: Pick<IncomeLineDto, 'schedule' | 'day1' | 'day2' | 'anchor'>): string {
  switch (l.schedule) {
    case 'monthly':
      return `Monthly on the ${ord(l.day1 ?? 1)}`;
    case 'twice_monthly':
      return `Twice a month: ${ord(l.day1 ?? 1)} and ${ord(l.day2 ?? 15)}`;
    case 'biweekly':
      return `Every two weeks from ${shortDate(l.anchor ?? '')}`;
    case 'weekly':
      return `Every week on ${weekday(l.anchor ?? '')}s`;
  }
}

/** "$926 left to budget", "Every dollar has a job" at exactly $0, "Over by $120" below it. */
export function leftToBudgetText(cents: number): string {
  if (cents === 0) return 'Every dollar has a job';
  return cents > 0 ? `${money(cents)} left to budget` : `Over by ${money(-cents)}`;
}

export const hasIncome = (d: OverviewDto) => d.income.some((l) => !l.archived);

/** Left to budget from what the Budget tab shows: income + carried over - planned spending - planned money set aside. */
export function leftToBudget(d: OverviewDto, planned: { spending: number; setAside: number }): number {
  return d.totals.incomeCents + d.totals.carryOverCents - planned.spending - planned.setAside;
}

export const CARRY_HINT = 'Money from last month you are adding to this month. Type it in; Hush does not work it out.';

/** The first line of a line's details, or "Add details". */
export const detailsPreview = (l: Pick<IncomeLineDto, 'details'>) => l.details?.split('\n').find((x) => x.trim())?.trim() || 'Add details';

/** An income line: name, amount and schedule only; its details sit in a row of their own beneath. */
function incomeRow(l: IncomeLineDto): string {
  return `<button class="brow bitem income" data-a="income-edit" data-id="${esc(l.id)}">
    <span><b>${esc(l.name)}</b> ${money(l.amountCents, 2)}<br><small class="muted">${esc(scheduleText(l))}</small></span>
    <span>${l.archived ? 'Archived' : money(l.month.totalCents)}<br><small class="muted">${l.archived ? '' : `${l.month.dates.length} this month`}</small></span></button>
  <button class="brow bitem details ${l.details ? '' : 'muted'}" data-a="income-details" data-id="${esc(l.id)}"><span>Details</span><span class="dprev">${esc(detailsPreview(l))}</span></button>`;
}

/** The details sheet: full height, a large box that keeps line breaks (up to 2000 characters). */
export function vDetailsSheet(line: IncomeLineDto): string {
  return `<div class="grab"></div><h2>${esc(line.name)} <small>details</small></h2>
  <input type="hidden" id="dt-id" value="${esc(line.id)}">
  <textarea id="dt-text" class="details" maxlength="2000" rows="16" placeholder="What makes up this paycheck: gross pay, taxes, 401k, insurance">${esc(line.details ?? '')}</textarea>
  <p class="hint">Up to 2000 characters. Line breaks are kept.</p>
  <div class="btns"><button class="btn primary" data-a="income-details-save">Save</button><button class="btn" data-a="close">Cancel</button></div>`;
}

/**
 * The Budget tab's headline, pinned at the top in a compact form while scrolling. main.ts updates
 * #ltb in place as amounts change, so the field being edited is never redrawn.
 */
export function budgetHeadline(d: OverviewDto, ltb: number): string {
  return hasIncome(d)
    ? `<div class="ltbhead sticky"><span class="big ${ltb < 0 ? 'neg' : ''}" id="ltb">${esc(leftToBudgetText(ltb))}</span></div>`
    : `<div class="ltbhead sticky"><span class="sub" id="ltb-none">Add your income to see left to budget.</span></div>`;
}

/** The Income group at the top of the Budget tab: its lines, Add income, and the carried-over line. */
export function vIncomeBudget(d: OverviewDto): string {
  const active = d.income.filter((l) => !l.archived);
  const archived = d.income.filter((l) => l.archived);
  return `${active.length ? '<p class="hint">Income and carried over, less everything planned below. What you spend does not change it.</p>' : ''}
  <p class="grp">Income</p>
  <div class="list">${active.map(incomeRow).join('')}
  <button class="brow bitem" data-a="income-add"><span>Add income</span><span>+</span></button>
  <div class="brow bitem"><label for="carry">Carried over from last month</label><div class="money"><span>$</span><input id="carry" data-carry="${esc(d.month.key)}" inputmode="decimal" value="${d.totals.carryOverCents / 100}"></div></div>
  <p class="hint carry">${esc(CARRY_HINT)}</p>
  <div class="btotal"><span>Income this month</span><span>${money(d.totals.incomeCents + d.totals.carryOverCents)}</span></div></div>
  ${archived.length ? `<details class="more"><summary>Archived income (${archived.length})</summary><div class="list">${archived.map(incomeRow).join('')}</div></details>` : ''}`;
}

/** The income line sheet: name, amount, schedule, starts in; archive and delete when editing. */
export function vIncomeSheet(d: OverviewDto, line: IncomeLineDto | null): string {
  const v = {
    schedule: line?.schedule ?? 'twice_monthly',
    day1: line?.day1 ?? 1,
    day2: line?.day2 ?? 15,
    anchor: line?.anchor ?? `${d.month.key}-01`,
  };
  const days = (id: string, sel: number) =>
    `<select id="${id}" class="sel">${Array.from({ length: 31 }, (_, i) => i + 1)
      .map((n) => `<option value="${n}" ${n === sel ? 'selected' : ''}>${ord(n)}</option>`)
      .join('')}</select>`;
  const opt = (value: string, label: string) => `<option value="${value}" ${v.schedule === value ? 'selected' : ''}>${label}</option>`;
  const show = (on: boolean) => (on ? '' : 'hidden');
  return `<div class="grab"></div><h2>${line ? 'Edit income' : 'Add income'}</h2>
  <input type="hidden" id="n-id" value="${esc(line?.id ?? '')}">
  <label class="fld"><span>Name</span><input id="n-name" type="text" maxlength="60" placeholder="Paycheck" value="${esc(line?.name ?? '')}"></label>
  <label class="fld"><span>Amount per paycheck, after taxes</span><div class="amt"><b>$</b><input id="n-amt" inputmode="decimal" placeholder="0.00" value="${line ? (line.amountCents / 100).toFixed(2) : ''}"></div></label>
  <label class="fld"><span>Paid</span><select id="n-schedule" class="sel">${opt('monthly', 'Once a month')}${opt('twice_monthly', 'Twice a month')}${opt('biweekly', 'Every two weeks')}${opt('weekly', 'Every week')}</select></label>
  <label class="fld" id="n-day1-f" ${show(v.schedule === 'monthly' || v.schedule === 'twice_monthly')}><span>On the</span>${days('n-day1', v.day1)}</label>
  <label class="fld" id="n-day2-f" ${show(v.schedule === 'twice_monthly')}><span>And the</span>${days('n-day2', v.day2)}</label>
  <label class="fld" id="n-anchor-f" ${show(v.schedule === 'biweekly' || v.schedule === 'weekly')}><span>One of its pay dates</span><input id="n-anchor" type="date" value="${esc(v.anchor)}"></label>
  <label class="fld"><span>Starts in</span><input id="n-starts" type="month" value="${esc(line?.startsMonth ?? d.month.key)}"></label>
  <p class="hint">It counts from this month on.${line ? ' Its details are in the row under it on the Budget tab.' : ''}</p>
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="income-save">${line ? 'Save changes' : 'Add income'}</button>${
    line
      ? `<button class="btn" data-a="income-archive" data-id="${esc(line.id)}" data-archived="${line.archived}">${line.archived ? 'Restore' : 'Archive'}</button><button class="btn danger" data-a="income-delete" data-id="${esc(line.id)}">Delete</button>`
      : ''
  }</div>
  ${line ? '<p class="hint">Archive keeps it in earlier months. Delete removes it from every month.</p>' : ''}`;
}

/** Overview: one line under left to spend, once there is income. */
export const overviewLeftToBudget = (d: OverviewDto) =>
  hasIncome(d) ? `<p class="sub ltb ${d.totals.leftToBudgetCents < 0 ? 'neg' : ''}">${esc(leftToBudgetText(d.totals.leftToBudgetCents))}</p>` : '';

/** Overview: "Add your income to see left to budget", until there is income or "Not now" (3 days). */
export const incomeCard = (d: OverviewDto) =>
  d.showIncomeCard
    ? `<section class="ask incomecard"><h2>Add your income to see left to budget</h2>
  <p class="hint">Your paychecks after taxes, on their schedule. Hush shows what is left once everything is planned.</p>
  <div class="acts"><button class="btn primary" data-a="income-add">Add income</button><button class="btn" data-a="income-snooze">Not now</button></div></section>`
    : '';
